// Producer repository - Supabase data access layer

import { supabase } from '@/integrations/supabase/client';
import { Producer, ProducerInsert, ProducerUpdate, ProducerBusinessStatus, VerificationStatus } from './types';

// Type for the producers table since it's not in the generated types yet
type ProducersTable = Producer;

export interface ListProducersParams {
  search?: string;
  status?: ('draft' | 'public')[];
  verified?: boolean | null;
  categories?: string[];
  producerStatus?: ProducerBusinessStatus[];
  verificationStatus?: VerificationStatus[];
}

/**
 * List all producers (admin only - RLS enforced)
 */
export async function listProducersAdmin(params: ListProducersParams = {}): Promise<Producer[]> {
  let query = supabase
    .from('producers')
    .select('*')
    .order('updated_at', { ascending: false });

  // Apply filters
  if (params.search) {
    const searchTerm = `%${params.search}%`;
    query = query.or(`name.ilike.${searchTerm},city.ilike.${searchTerm},region.ilike.${searchTerm}`);
  }

  if (params.status && params.status.length > 0) {
    query = query.in('status', params.status);
  }

  if (params.verified !== null && params.verified !== undefined) {
    query = query.eq('verified', params.verified);
  }

  if (params.producerStatus && params.producerStatus.length > 0) {
    query = query.in('producer_status', params.producerStatus);
  }

  // Verification status filter
  if (params.verificationStatus && params.verificationStatus.length > 0) {
    // Build OR conditions for each verification status
    const conditions: string[] = [];
    for (const vs of params.verificationStatus) {
      if (vs === 'verified') {
        conditions.push('verified.eq.true');
      } else if (vs === 'pending') {
        conditions.push('and(verified.eq.false,verification_submitted_at.not.is.null)');
      } else if (vs === 'not_submitted') {
        conditions.push('and(verified.eq.false,verification_submitted_at.is.null)');
      }
    }
    if (conditions.length > 0) {
      query = query.or(conditions.join(','));
    }
  }

  // Categories filter handled client-side for array overlap

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching producers:', error);
    throw error;
  }

  let producers = (data as unknown as ProducersTable[]) || [];

  // Client-side category filtering (for array overlap)
  if (params.categories && params.categories.length > 0) {
    producers = producers.filter((p) =>
      params.categories!.some((cat) => p.categories?.includes(cat))
    );
  }

  return producers;
}

/**
 * Get a single producer by ID (admin only - RLS enforced)
 */
export async function getProducerAdmin(id: string): Promise<Producer | null> {
  const { data, error } = await supabase
    .from('producers')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching producer:', error);
    throw error;
  }

  return data as unknown as Producer | null;
}

/**
 * Create a new producer (admin only - RLS enforced)
 */
export async function createProducer(producer: ProducerInsert): Promise<Producer> {
  const { data, error } = await supabase
    .from('producers')
    .insert(producer as any)
    .select()
    .single();

  if (error) {
    console.error('Error creating producer:', error);
    throw error;
  }

  return data as unknown as Producer;
}

/**
 * Update a producer (admin only - RLS enforced)
 */
export async function updateProducer(id: string, updates: ProducerUpdate): Promise<Producer> {
  const { data, error } = await supabase
    .from('producers')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating producer:', error);
    throw error;
  }

  return data as unknown as Producer;
}

/**
 * Delete a producer (admin only - RLS enforced)
 */
export async function deleteProducer(id: string): Promise<void> {
  const { error } = await supabase
    .from('producers')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting producer:', error);
    throw error;
  }
}

/**
 * List public producers (for Explore/Detail pages)
 * RLS ensures only status='public' are returned, but we filter explicitly too
 */
export async function listPublicProducers(): Promise<Producer[]> {
  const { data, error } = await supabase
    .from('producers')
    .select('*')
    .eq('status', 'public')
    .order('name', { ascending: true });

  if (error) {
    console.error('Error fetching public producers:', error);
    throw error;
  }

  return (data as unknown as ProducersTable[]) || [];
}

/**
 * Get a public producer by ID or slug
 */
export async function getPublicProducer(idOrSlug: string): Promise<Producer | null> {
  // Try by ID first
  let { data, error } = await supabase
    .from('producers')
    .select('*')
    .eq('id', idOrSlug)
    .eq('status', 'public')
    .maybeSingle();

  // If not found, try by slug
  if (!data && !error) {
    const result = await supabase
      .from('producers')
      .select('*')
      .eq('slug', idOrSlug)
      .eq('status', 'public')
      .maybeSingle();
    
    data = result.data;
    error = result.error;
  }

  if (error) {
    console.error('Error fetching public producer:', error);
    throw error;
  }

  return data as unknown as Producer | null;
}

/**
 * Check if the current user is an admin
 */
export async function checkIsAdmin(): Promise<boolean> {
  // Prefer backend source-of-truth.
  // This relies on an RPC function `is_admin()` in the backend.
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    console.error('Error getting session:', sessionError);
    return false;
  }

  if (!session) return false;

  const { data, error } = await supabase.rpc('is_admin');
  if (error) {
    console.error('Error checking admin status (rpc is_admin):', error);
    return false;
  }

  return Boolean(data);
}

/**
 * Admin RPC to set producer status (business status)
 * Logs the change to admin_audit_log
 */
export async function adminSetProducerStatus(
  producerId: string,
  status: ProducerBusinessStatus
): Promise<{ id: string; producer_status: ProducerBusinessStatus; old_status: ProducerBusinessStatus }> {
  const { data, error } = await supabase.rpc('admin_set_producer_status', {
    p_producer_id: producerId,
    p_status: status,
  });

  if (error) {
    console.error('Error setting producer status:', error);
    throw error;
  }

  return data as { id: string; producer_status: ProducerBusinessStatus; old_status: ProducerBusinessStatus };
}
